// pages/PrivacyPolicy.jsx
import React from "react";

export default function PrivacyPolicy() {
  return (
    <div className="panel">
      <h2>Privacy Policy</h2>
      <p>
        This Privacy Policy describes how <strong>Gluten Free Navigator</strong> ("we," "our," or "us")
        collects, uses, and protects your personal information when you use our website
        (<a href="https://gfnavigator.com">gfnavigator.com</a>) and related services.
        By using our website, you agree to the terms of this Privacy Policy.
      </p>

      <h3>1. Who We Are</h3>
      <p>
        Gluten Free Navigator is based in California, USA.  
        You can reach us at{" "}
        <a href="mailto:contact@gfnavigator.com">contact@gfnavigator.com</a> for any
        privacy-related questions or requests.
      </p>

      <h3>2. Information We Collect</h3>
      <p>We collect the following types of information when you use our service:</p>
      <ul>
        <li>
          <strong>Account Data:</strong> First name, last name, email address, password
          (encrypted), and optional ZIP code.
        </li>
        <li>
          <strong>Location Data:</strong> ZIP code or browser location coordinates when you
          use the “Use My Location” feature. Coordinates are used only for search results
          and not stored permanently.
        </li>
        <li>
          <strong>Device and Usage Data:</strong> IP address, browser type, and user-agent,
          which help us secure the platform and analyze usage.
        </li>
        <li>
          <strong>Feedback Data:</strong> Information you submit through our feedback widget
          or contact forms.
        </li>
        <li>
          <strong>Cookies:</strong> We use cookies to maintain login sessions, measure site
          traffic, and improve our service experience.
        </li>
      </ul>
      <p>We do not collect or store any sensitive personal data (e.g., medical or allergy information).</p>

      <h3>3. How We Use Your Information</h3>
      <p>We use your data to:</p>
      <ul>
        <li>Provide and personalize the Gluten Free Navigator service;</li>
        <li>Enable account creation and login functionality;</li>
        <li>Perform search personalization and improve restaurant listings;</li>
        <li>Analyze traffic and usage trends through Google Analytics;</li>
        <li>Communicate updates, offers, or marketing messages (you can unsubscribe anytime);</li>
        <li>Maintain security, detect fraud, and comply with legal obligations.</li>
      </ul>

      <h3>4. How We Share Information</h3>
      <p>
        We do not sell or rent your personal information.  
        We may share limited data with:
      </p>
      <ul>
        <li>
          <strong>Service Providers:</strong> Hosting and infrastructure partners such as
          Google Cloud and Amazon Web Services (AWS) who help us operate the site.
        </li>
        <li>
          <strong>Analytics:</strong> Google Analytics and Google Ads for traffic and
          performance measurement.
        </li>
        <li>
          <strong>Payment Processing:</strong> Stripe securely handles all payment
          information. We do not store or have access to your credit card details.
        </li>
      </ul>

      <h3>5. Marketing Communications</h3>
      <p>
        If you opt in to receive emails, we may send you updates and offers.
        You can unsubscribe anytime by clicking the "Unsubscribe" link in any marketing email.
      </p>

      <h3>6. Data Storage and Security</h3>
      <p>
        We host data securely on Google Cloud and AWS. Passwords are encrypted, and all
        data transmissions occur over HTTPS.  
        We keep your account information for as long as you have an active account.  
        You can request deletion of your data or account anytime by emailing{" "}
        <a href="mailto:contact@gfnavigator.com">contact@gfnavigator.com</a>.
      </p>

      <h3>7. Your Rights</h3>
      <p>
        Depending on where you live, you may have rights under data-protection laws such as
        the GDPR (UK) or CCPA (California).  
        These may include the right to:
      </p>
      <ul>
        <li>Access, correct, or delete your personal data;</li>
        <li>Withdraw consent for marketing communications;</li>
        <li>Request a copy of your data (“data portability”).</li>
      </ul>
      <p>
        To exercise any of these rights, please contact us at{" "}
        <a href="mailto:contact@gfnavigator.com">contact@gfnavigator.com</a>.
      </p>

      <h3>8. International Users</h3>
      <p>
        Gluten Free Navigator is based in the United States but welcomes users from the
        United Kingdom. By using our site, you acknowledge that your information may be
        processed and stored in the U.S. where data-protection laws may differ from those in
        your country.
      </p>

      <h3>9. Children’s Privacy</h3>
      <p>
        Our services are not intended for children under 13 years old. We do not knowingly
        collect personal information from minors. If you believe a child has provided us
        with personal data, please contact us to remove it.
      </p>

      <h3>10. Changes to This Policy</h3>
      <p>
        We may update this Privacy Policy periodically. When we do, we’ll send a notice by
        email and update the “Last updated” date below. Your continued use of the site
        after changes take effect means you agree to the new policy.
      </p>

      <p className="help">Last updated: November 2025</p>
    </div>
  );
}
